// src/services/tradingService.ts
import axios from 'axios';
import { 
  ActiveTrade, 
  OrderData, 
  TradingApiResponse,
  MarketStats,
  TradeHistory,
  TradeRecommendation,
  MarketDataPoint
} from '../providers/tradingTypes';
import { getMockMarketData, updateMockMarketData } from '../providers/marketDataProvider';
import { getHatateToken } from '../helpers/utils';

// Flag para usar datos mock durante desarrollo
const USE_MOCK_DATA = process.env.NODE_ENV === 'development' || true;

// Configuración del endpoint de IA
const API_BASE_URL = 'https://backend.anubisai.net/api';
const LLM_ENDPOINT = `${API_BASE_URL}/bot/llm`;

/**
 * Función principal para enviar consultas al LLM
 */
const requestLLM = async (contextualPrompt: string, additionalContext: any = {}) => {
  try {
    const apiToken = getHatateToken();
    const timestamp = new Date().toISOString();
    
    const response = await fetch(LLM_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${apiToken}`
      },
      body: JSON.stringify({
        prompt: [{
          content: contextualPrompt,
          role: 'user',
          time: timestamp
        }],
        chatId: `trading_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        skill: 'ambipar_v1',
        module: 'ambipar_hazmat_tech',
        // Incluir contexto adicional relevante para trading
        contextData: additionalContext
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Error en la solicitud: ${response.status}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      data: data,
      text: data.response || data.text || data.content,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error al consultar LLM:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Error desconocido',
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Solicitar respuesta en streaming del LLM
 */
export const requestLLMStream = async (
  contextualPrompt: string,
  additionalContext: any = {},
  onChunk: (chunk: string) => void,
  onComplete: (response: any) => void
) => {
  try {
    const apiToken = getHatateToken();
    const timestamp = new Date().toISOString();
    
    const response = await fetch(LLM_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${apiToken}`
      },
      body: JSON.stringify({
        prompt: [{
          content: contextualPrompt,
          role: 'user',
          time: timestamp
        }],
        chatId: `trading_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        skill: 'ambipar_v1',
        module: 'ambipar_hazmat_tech',
        contextData: additionalContext,
        stream: true // Indicar que queremos streaming
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Error en la solicitud: ${response.status}`);
    }
    
    if (!response.body) {
      throw new Error('No se recibió un stream como respuesta');
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulatedText = '';
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value, { stream: true });
      accumulatedText += chunk;
      
      // Notificar sobre cada actualización
      onChunk(chunk);
    }
    
    // Intentamos parsear el resultado
    try {
      const jsonResult = JSON.parse(accumulatedText);
      onComplete({
        success: true,
        data: jsonResult,
        text: jsonResult.response || jsonResult.text || jsonResult.content,
        timestamp: new Date().toISOString()
      });
    } catch (e) {
      // Si no es JSON, devolvemos el texto
      onComplete({
        success: true,
        text: accumulatedText,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('Error en streaming LLM:', error);
    onComplete({
      success: false,
      message: error instanceof Error ? error.message : 'Error desconocido',
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Obtiene datos de mercado para un símbolo y timeframe específicos
 */
export const getMarketData = async (
  symbol: string, 
  timeframe: string = '1h'
): Promise<MarketDataPoint[]> => {
  try {
    // Si estamos en modo de desarrollo, usar datos simulados
    if (USE_MOCK_DATA) {
      const mockData = getMockMarketData(symbol, timeframe as any);
      return mockData.data;
    }
    
    // En producción, consultar al LLM para obtener datos históricos
    const prompt = `Por favor proporciona datos históricos de precio para ${symbol} en timeframe ${timeframe}. 
    Necesito los últimos 100 puntos con timestamp, precio, volumen, máximo y mínimo en formato JSON.`;
    
    const response = await requestLLM(prompt, { 
      dataType: 'marketData',
      symbol,
      timeframe
    });
    
    if (response.success && response.data) {
      // Intentar extraer los datos del mercado de la respuesta
      if (Array.isArray(response.data.marketData)) {
        return response.data.marketData;
      }
      
      // Si hay un campo data en la respuesta
      if (Array.isArray(response.data.data)) {
        return response.data.data;
      }
      
      // Intentar extraer datos de JSON en el texto
      if (response.text) {
        try {
          // Buscar estructura JSON en la respuesta de texto
          const jsonMatch = response.text.match(/\[\s*\{[\s\S]*\}\s*\]/);
          if (jsonMatch) {
            const parsedData = JSON.parse(jsonMatch[0]);
            if (Array.isArray(parsedData)) {
              return parsedData;
            }
          }
        } catch (e) {
          console.error('Error al parsear JSON en respuesta:', e);
        }
      }
    }
    
    console.warn('No se pudieron obtener datos reales, utilizando datos simulados');
    const mockData = getMockMarketData(symbol, timeframe as any);
    return mockData.data;
  } catch (error) {
    console.error('Error al obtener datos del mercado:', error);
    
    // En caso de error, devolver datos simulados
    const mockData = getMockMarketData(symbol, timeframe as any);
    return mockData.data;
  }
};

/**
 * Simula la actualización de datos de mercado (para uso con polling)
 */
export const updateMarketData = (
  symbol: string, 
  timeframe: string = '1h'
): MarketDataPoint[] => {
  if (USE_MOCK_DATA) {
    const updatedData = updateMockMarketData(symbol, timeframe as any);
    return updatedData.data;
  }
  
  // En producción, esto sería reemplazado por una nueva llamada al LLM
  // pero para simplificar el polling, usamos datos simulados
  const updatedData = updateMockMarketData(symbol, timeframe as any);
  return updatedData.data;
};

/**
 * Obtiene el precio actual (ticker) de un símbolo
 */
export const getCurrentPrice = async (symbol: string): Promise<number> => {
  try {
    if (USE_MOCK_DATA) {
      const mockData = getMockMarketData(symbol);
      const lastDataPoint = mockData.data[mockData.data.length - 1];
      return lastDataPoint.price;
    }
    
    // Consultar al LLM para obtener el precio actual
    const prompt = `¿Cuál es el precio actual de ${symbol}? Por favor responde solo con el valor numérico.`;
    
    const response = await requestLLM(prompt, { 
      dataType: 'currentPrice',
      symbol
    });
    
    if (response.success && response.text) {
      // Extraer el precio del texto
      const priceRegex = /\$?(\d+[.,]?\d*)/;
      const match = response.text.match(priceRegex);
      if (match) {
        return parseFloat(match[1].replace(',', '.'));
      }
    }
    
    console.warn('No se pudo obtener precio real, utilizando precio simulado');
    const mockData = getMockMarketData(symbol);
    return mockData.data[mockData.data.length - 1].price;
  } catch (error) {
    console.error('Error al obtener precio actual:', error);
    
    // En caso de error, usar último precio simulado
    const mockData = getMockMarketData(symbol);
    return mockData.data[mockData.data.length - 1].price;
  }
};

/**
 * Obtiene estadísticas del mercado para un símbolo
 */
export const getMarketStats = async (symbol: string): Promise<MarketStats> => {
  try {
    if (USE_MOCK_DATA) {
      return generateMockMarketStats(symbol);
    }
    
    // Consultar al LLM para obtener estadísticas del mercado
    const prompt = `Proporciona un resumen de las estadísticas de mercado para ${symbol} con el precio actual, 
    cambio en 24 horas, máximo, mínimo y volumen en 24 horas. Responde en formato JSON.`;
    
    const response = await requestLLM(prompt, { 
      dataType: 'marketStats',
      symbol
    });
    
    if (response.success && response.data && response.data.stats) {
      return response.data.stats;
    }
    
    console.warn('No se pudieron obtener estadísticas reales, utilizando datos simulados');
    return generateMockMarketStats(symbol);
  } catch (error) {
    console.error('Error al obtener estadísticas del mercado:', error);
    
    // Generar datos simulados en caso de error
    return generateMockMarketStats(symbol);
  }
};

/**
 * Obtiene operaciones activas
 */
export const getActiveTrades = async (): Promise<ActiveTrade[]> => {
  try {
    if (USE_MOCK_DATA) {
      return generateMockActiveTrades();
    }
    
    // Consultar al LLM para obtener operaciones activas
    const prompt = `Muestra mis operaciones de trading activas con todos los detalles incluyendo 
    símbolo, tipo, precio de entrada, precio actual, ganancia/pérdida, etc. Responde en formato JSON.`;
    
    const response = await requestLLM(prompt, { 
      dataType: 'activeTrades'
    });
    
    if (response.success && response.data && Array.isArray(response.data.trades)) {
      return response.data.trades;
    }
    
    console.warn('No se pudieron obtener operaciones reales, utilizando datos simulados');
    return generateMockActiveTrades();
  } catch (error) {
    console.error('Error al obtener operaciones activas:', error);
    return generateMockActiveTrades();
  }
};

/**
 * Obtiene historial de operaciones
 */
export const getTradeHistory = async (): Promise<TradeHistory[]> => {
  try {
    if (USE_MOCK_DATA) {
      return generateMockTradeHistory();
    }
    
    // Consultar al LLM para obtener historial de operaciones
    const prompt = `Muestra mi historial de operaciones de trading cerradas con todos los detalles 
    incluyendo símbolo, tipo, precio de entrada, precio de salida, ganancia/pérdida, etc. Responde en formato JSON.`;
    
    const response = await requestLLM(prompt, { 
      dataType: 'tradeHistory'
    });
    
    if (response.success && response.data && Array.isArray(response.data.trades)) {
      return response.data.trades;
    }
    
    console.warn('No se pudo obtener historial real, utilizando datos simulados');
    return generateMockTradeHistory();
  } catch (error) {
    console.error('Error al obtener historial de operaciones:', error);
    return generateMockTradeHistory();
  }
};

/**
 * Ejecuta una orden de trading
 */
export const executeOrder = async (orderData: OrderData): Promise<TradingApiResponse<any>> => {
  try {
    if (USE_MOCK_DATA) {
      // Simular éxito de la operación
      return {
        success: true,
        message: 'Orden ejecutada correctamente',
        data: {
          orderId: `order-${Date.now()}`,
          status: 'executed',
          ...orderData,
          timestamp: new Date().toISOString()
        },
        timestamp: new Date().toISOString()
      };
    }
    
    // Consultar al LLM para ejecutar una orden
    const prompt = `Ejecuta una orden de ${orderData.action} para ${orderData.symbol} a precio ${orderData.price}, 
    cantidad ${orderData.amount}${orderData.stopLoss ? `, stop loss ${orderData.stopLoss}` : ''}
    ${orderData.takeProfit ? `, take profit ${orderData.takeProfit}` : ''}.`;
    
    const response = await requestLLM(prompt, { 
      dataType: 'executeOrder',
      orderData
    });
    
    if (response.success) {
      return {
        success: true,
        message: 'Orden procesada',
        data: {
          orderId: `order-${Date.now()}`,
          status: 'executed',
          ...orderData,
          timestamp: new Date().toISOString()
        },
        timestamp: new Date().toISOString()
      };
    } else {
      return {
        success: false,
        message: response.message || 'Error al ejecutar la orden',
        timestamp: new Date().toISOString()
      };
    }
  } catch (error) {
    console.error('Error al ejecutar orden:', error);
    
    return {
      success: false,
      message: 'Error al ejecutar la orden. Por favor, inténtalo de nuevo.',
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Activa o desactiva el bot de trading
 */
export const toggleTradingBot = async (active: boolean): Promise<TradingApiResponse<any>> => {
  try {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        message: active ? 'Bot de trading activado' : 'Bot de trading desactivado',
        timestamp: new Date().toISOString()
      };
    }
    
    // Consultar al LLM para activar/desactivar el bot
    const prompt = active 
      ? "Activa el bot de trading para monitorear el mercado y generar recomendaciones automáticas." 
      : "Desactiva el bot de trading y detén todas las operaciones automáticas.";
    
    const response = await requestLLM(prompt, { 
      dataType: 'toggleBot',
      active
    });
    
    return {
      success: response.success,
      message: response.message || (active ? 'Bot activado' : 'Bot desactivado'),
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error al cambiar estado del bot:', error);
    
    return {
      success: false,
      message: 'Error al cambiar estado del bot. Por favor, inténtalo de nuevo.',
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Actualiza la configuración del bot de trading
 */
export const updateBotConfig = async (config: any): Promise<TradingApiResponse<any>> => {
  try {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        message: 'Configuración del bot actualizada',
        timestamp: new Date().toISOString()
      };
    }
    
    // Construir mensaje para actualizar configuración
    const configDescription = Object.entries(config)
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join(', ');
    
    // Consultar al LLM para actualizar configuración
    const prompt = `Actualiza la configuración del bot de trading con los siguientes parámetros: ${configDescription}`;
    
    const response = await requestLLM(prompt, { 
      dataType: 'updateConfig',
      config
    });
    
    return {
      success: response.success,
      message: response.message || 'Configuración actualizada',
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error al actualizar configuración del bot:', error);
    
    return {
      success: false,
      message: 'Error al actualizar configuración. Por favor, inténtalo de nuevo.',
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Obtiene recomendaciones previas del bot
 */
export const getPreviousRecommendations = async (
  symbol: string
): Promise<TradeRecommendation[]> => {
  try {
    if (USE_MOCK_DATA) {
      return generateMockRecommendations(symbol);
    }
    
    // Consultar al LLM para obtener recomendaciones previas
    const prompt = `Muestra las recomendaciones de trading previas para ${symbol} con todos los detalles como 
    acción, precio, stop loss, take profit, confianza, razón e indicadores usados. Responde en formato JSON.`;
    
    const response = await requestLLM(prompt, { 
      dataType: 'previousRecommendations',
      symbol
    });
    
    if (response.success && response.data && Array.isArray(response.data.recommendations)) {
      return response.data.recommendations;
    }
    
    if (response.success && response.text) {
      // Intentar extraer recomendaciones del texto
      return extractRecommendationsFromText(response.text, symbol);
    }
    
    console.warn('No se pudieron obtener recomendaciones reales, utilizando datos simulados');
    return generateMockRecommendations(symbol);
  } catch (error) {
    console.error('Error al obtener recomendaciones previas:', error);
    return generateMockRecommendations(symbol);
  }
};

/**
 * Obtiene una recomendación de trading
 */
export const getTradeRecommendation = async (
  symbol: string,
  timeframe: string = '1h',
  marketData?: MarketDataPoint[]
): Promise<TradeRecommendation> => {
  try {
    if (USE_MOCK_DATA) {
      const mockRecommendations = generateMockRecommendations(symbol);
      return mockRecommendations[0]; // Devolver la primera recomendación
    }
    
    // Preparar datos de mercado resumidos si están disponibles
    let marketSummary = '';
    if (marketData && marketData.length > 0) {
      const lastPrice = marketData[marketData.length - 1].price;
      const firstPrice = marketData[0].price;
      const changePercent = ((lastPrice - firstPrice) / firstPrice) * 100;
      
      // Extraer máximos y mínimos
      const prices = marketData.map(p => p.price);
      const high = Math.max(...prices);
      const low = Math.min(...prices);
      
      marketSummary = `
        Precio actual: ${lastPrice}
        Cambio: ${changePercent.toFixed(2)}%
        Máximo: ${high}
        Mínimo: ${low}
      `;
    }
    
    // Consultar al LLM para obtener recomendación
    const prompt = `Analiza el mercado de ${symbol} en timeframe ${timeframe} y proporciona una recomendación de trading detallada.
    
    ${marketSummary ? `DATOS DE MERCADO:\n${marketSummary}` : ''}
    
    Por favor, proporciona tu recomendación con el siguiente formato:
    1. ACCIÓN: [BUY/SELL/HOLD]
    2. PRECIO DE ENTRADA: [precio sugerido]
    3. STOP LOSS: [nivel de stop loss]
    4. TAKE PROFIT: [nivel de take profit]
    5. CONFIANZA: [nivel de confianza del 0-100]
    6. RAZÓN: [explicación de la recomendación]
    7. INDICADORES CLAVE: [lista de indicadores relevantes]`;
    
    const response = await requestLLM(prompt, { 
      dataType: 'tradeRecommendation',
      symbol,
      timeframe,
      marketSummary
    });
    
    if (response.success && response.text) {
      return extractRecommendationFromText(response.text, symbol);
    }
    
    console.warn('No se pudo obtener recomendación real, utilizando datos simulados');
    const mockRecommendations = generateMockRecommendations(symbol);
    return mockRecommendations[0];
  } catch (error) {
    console.error('Error al obtener recomendación:', error);
    
    // Crear recomendación por defecto en caso de error
    return {
      id: `rec-error-${Date.now()}`,
      symbol,
      action: 'HOLD',
      price: marketData ? marketData[marketData.length - 1].price : 0,
      confidence: 50,
      timestamp: new Date().toISOString(),
      reason: 'Error al consultar recomendación. Manteniendo posición actual.',
      stopLoss: 0,
      takeProfit: 0,
      indicators: []
    };
  }
};

// Funciones auxiliares para procesar datos

/**
 * Extrae una recomendación de trading desde texto
 */
const extractRecommendationFromText = (text: string, symbol: string): TradeRecommendation => {
  // Buscar la acción (BUY/SELL/HOLD)
  const actionRegex = /acci[oóò]n:?\s*(COMPRAR?|VENDER?|MANTENER|BUY|SELL|HOLD)/i;
  const actionMatch = text.match(actionRegex);
  const action = mapAction(actionMatch ? actionMatch[1].toUpperCase() : "HOLD");
  
  // Buscar el precio de entrada
  const priceRegex = /precio\s*(?:de\s*entrada)?:?\s*[$]?\s*(\d+[.,]?\d*)/i;
  const priceMatch = text.match(priceRegex);
  const price = priceMatch ? parseFloat(priceMatch[1].replace(',', '.')) : 0;
  
  // Buscar stop loss
  const stopLossRegex = /stop\s*loss:?\s*[$]?\s*(\d+[.,]?\d*)/i;
  const stopLossMatch = text.match(stopLossRegex);
  const stopLoss = stopLossMatch ? parseFloat(stopLossMatch[1].replace(',', '.')) : 0;
  
  // Buscar take profit
  const takeProfitRegex = /take\s*profit:?\s*[$]?\s*(\d+[.,]?\d*)/i;
  const takeProfitMatch = text.match(takeProfitRegex);
  const takeProfit = takeProfitMatch ? parseFloat(takeProfitMatch[1].replace(',', '.')) : 0;
  
  // Buscar confianza
  const confidenceRegex = /confianza:?\s*(\d+)(?:\s*%)?/i;
  const confidenceMatch = text.match(confidenceRegex);
  const confidence = confidenceMatch ? parseInt(confidenceMatch[1]) : 50;
  
  // Buscar razón/explicación
  const reasonRegex = /raz[oóò]n:?\s*(.+?)(?:\n\d|\n$|$)/i;
  const reasonMatch = text.match(reasonRegex);
  let reason = reasonMatch ? reasonMatch[1].trim() : "";
  
  // Si no encontramos una razón específica, usar parte del texto
  if (!reason) {
    // Buscar la explicación más larga entre párrafos
    const paragraphs = text.split(/\n+/);
    let longestParagraph = "";
    
    for (const paragraph of paragraphs) {
      if (paragraph.length > longestParagraph.length && 
          !paragraph.includes("ACCIÓN:") && 
          !paragraph.includes("PRECIO") &&
          !paragraph.includes("STOP LOSS") && 
          !paragraph.includes("TAKE PROFIT") &&
          !paragraph.includes("CONFIANZA")) {
        longestParagraph = paragraph;
      }
    }
    
    reason = longestParagraph || "Análisis basado en condiciones actuales del mercado";
  }
  
  // Extraer indicadores
  const indicators: any[] = [];
  
  // Buscar RSI
  const rsiRegex = /RSI\s*(?:\(?\d+\)?)?:?\s*(\d+[.,]?\d*)/i;
  const rsiMatch = text.match(rsiRegex);
  if (rsiMatch) {
    indicators.push({
      name: "RSI",
      value: parseFloat(rsiMatch[1].replace(',', '.'))
    });
  }
  
  // Buscar medias móviles
  const maRegex = /MA\s*(\d+):?\s*(\d+[.,]?\d*)/ig;
  let maMatch;
  while ((maMatch = maRegex.exec(text)) !== null) {
    indicators.push({
      name: `MA${maMatch[1]}`,
      value: parseFloat(maMatch[2].replace(',', '.'))
    });
  }
  
  return {
    id: `rec-${Date.now()}`,
    symbol,
    action,
    price,
    stopLoss,
    takeProfit,
    confidence,
    reason,
    indicators,
    timestamp: new Date().toISOString()
  };
};

/**
 * Extrae múltiples recomendaciones de trading desde texto
 */
const extractRecommendationsFromText = (text: string, symbol: string): TradeRecommendation[] => {
  // Esta función intentaría extraer múltiples recomendaciones de un texto
  // Para simplificar, extraemos solo una recomendación
  const recommendation = extractRecommendationFromText(text, symbol);
  return [recommendation];
};

/**
 * Mapea acciones en español/inglés a formato estándar
 */
const mapAction = (action: string): 'BUY' | 'SELL' | 'HOLD' => {
  const normalizedAction = action.toUpperCase().trim();
  
  if (normalizedAction === 'COMPRAR' || normalizedAction === 'COMPRA' || normalizedAction === 'BUY') {
    return 'BUY';
  }
  
  if (normalizedAction === 'VENDER' || normalizedAction === 'VENTA' || normalizedAction === 'SELL') {
    return 'SELL';
  }
  
  return 'HOLD'; // Valor por defecto
};

// Funciones para generar datos simulados

/**
 * Genera estadísticas de mercado simuladas
 */
const generateMockMarketStats = (symbol: string): MarketStats => {
  const mockData = getMockMarketData(symbol);
  const lastDataPoint = mockData.data[mockData.data.length - 1];
  const firstDataPoint = mockData.data[0];
  
  const change = lastDataPoint.price - firstDataPoint.price;
  const changePercentage = (change / firstDataPoint.price) * 100;
  
  // Encontrar máximo y mínimo
  const prices = mockData.data.map(item => item.price);
  const high24h = Math.max(...prices);
  const low24h = Math.min(...prices);
  
  // Volumen acumulado
  const volume24h = mockData.data.reduce((total, item) => total + (item.volume || 0), 0);
  
  return {
    symbol,
    price: lastDataPoint.price,
    change24h: parseFloat(change.toFixed(2)),
    changePercentage24h: parseFloat(changePercentage.toFixed(2)),
    high24h,
    low24h,
    volume24h,
    lastUpdate: new Date().toISOString()
  };
};

/**
 * Genera operaciones activas simuladas
 */
const generateMockActiveTrades = (): ActiveTrade[] => {
  const symbols = ['BTC/USD', 'ETH/USD', 'XRP/USD', 'SOL/USD'];
  const trades: ActiveTrade[] = [];
  
  // Generar entre 0 y 3 operaciones activas aleatorias
  const numTrades = Math.floor(Math.random() * 4);
  
  for (let i = 0; i < numTrades; i++) {
    const symbol = symbols[Math.floor(Math.random() * symbols.length)];
    const type = Math.random() > 0.5 ? 'BUY' : 'SELL';
    const mockData = getMockMarketData(symbol);
    const currentPrice = mockData.data[mockData.data.length - 1].price;
    
    // Generar un precio de entrada realista
    const entryPriceFactor = 1 + (Math.random() * 0.1 - 0.05); // ±5%
    const entryPrice = currentPrice * entryPriceFactor;
    
    // Calcular PnL
    const pnl = type === 'BUY' 
      ? (currentPrice - entryPrice) * Math.random() * 2 // Cantidad aleatoria
      : (entryPrice - currentPrice) * Math.random() * 2;
      
    const pnlPercentage = (pnl / entryPrice) * 100;
    
    // Generar stop loss y take profit
    const stopLoss = type === 'BUY' 
      ? entryPrice * 0.9
      : entryPrice * 1.1;
      
    const takeProfit = type === 'BUY'
      ? entryPrice * 1.2
      : entryPrice * 0.8;
    
    trades.push({
      id: `trade-${i}-${Date.now()}`,
      symbol,
      type,
      entryPrice,
      currentPrice,
      amount: Math.random() * (symbol === 'BTC/USD' ? 1 : 10), // Cantidad apropiada según el activo
      openTime: new Date(Date.now() - Math.random() * 86400000 * 5).toISOString(), // Hasta 5 días atrás
      pnl,
      pnlPercentage,
      stopLoss,
      takeProfit
    });
  }
  
  return trades;
};

/**
 * Genera historial de operaciones simulado
 */
const generateMockTradeHistory = (): TradeHistory[] => {
  const symbols = ['BTC/USD', 'ETH/USD', 'XRP/USD', 'SOL/USD'];
  const trades: TradeHistory[] = [];
  
  // Generar entre 3 y 8 operaciones históricas
  const numTrades = 3 + Math.floor(Math.random() * 6);
  
  for (let i = 0; i < numTrades; i++) {
    const symbol = symbols[Math.floor(Math.random() * symbols.length)];
    const type = Math.random() > 0.5 ? 'BUY' : 'SELL';
    
    // Obtener precios históricos
    const mockData = getMockMarketData(symbol);
    const index = Math.floor(Math.random() * (mockData.data.length - 10));
    const entryPrice = mockData.data[index].price;
    const exitIndex = index + 1 + Math.floor(Math.random() * 9); // 1-10 barras después
    const exitPrice = mockData.data[exitIndex].price;
    
    // Calcular PnL
    const amount = Math.random() * (symbol === 'BTC/USD' ? 1 : 10);
    const pnl = type === 'BUY' 
      ? (exitPrice - entryPrice) * amount
      : (entryPrice - exitPrice) * amount;
      
    const pnlPercentage = (pnl / entryPrice) * 100;
    
    // Stop loss y take profit
    const stopLoss = type === 'BUY' 
      ? entryPrice * 0.9
      : entryPrice * 1.1;
      
    const takeProfit = type === 'BUY'
      ? entryPrice * 1.2
      : entryPrice * 0.8;
    
    // Fechas
    const closeTime = new Date(Date.now() - Math.random() * 86400000 * 30).toISOString(); // Último mes
    const openTime = new Date(new Date(closeTime).getTime() - Math.random() * 86400000 * 5).toISOString(); // 1-5 días antes
    
    trades.push({
      id: `hist-${i}-${Date.now()}`,
      symbol,
      type,
      entryPrice,
      exitPrice,
      amount,
      openTime,
      closeTime,
      pnl,
      pnlPercentage,
      stopLoss,
      takeProfit,
      status: 'closed'
    });
  }
  
  // Ordenar por fecha de cierre (más reciente primero)
  return trades.sort((a, b) => 
    new Date(b.closeTime).getTime() - new Date(a.closeTime).getTime()
  );
};

/**
 * Genera recomendaciones simuladas
 */
const generateMockRecommendations = (symbol: string): TradeRecommendation[] => {
  const recommendations: TradeRecommendation[] = [];
  const actions: Array<'BUY' | 'SELL' | 'HOLD'> = ['BUY', 'SELL', 'HOLD'];
  
  // Obtener precio actual para hacer recomendaciones realistas
  const mockData = getMockMarketData(symbol);
  const currentPrice = mockData.data[mockData.data.length - 1].price;
  
  // Generar entre 2 y 5 recomendaciones
  const numRecommendations = 2 + Math.floor(Math.random() * 4);
  
  for (let i = 0; i < numRecommendations; i++) {
    const action = actions[Math.floor(Math.random() * actions.length)];
    
    // Precio con ligera variación
    const price = currentPrice * (1 + (Math.random() * 0.04 - 0.02)); // ±2%
    
    // Stop loss y take profit según acción
    const stopLoss = action === 'BUY' 
      ? price * (0.92 + Math.random() * 0.03) // 92-95% del precio
      : price * (1.05 + Math.random() * 0.03); // 105-108% del precio
      
    const takeProfit = action === 'BUY'
      ? price * (1.1 + Math.random() * 0.1) // 110-120% del precio
      : price * (0.85 + Math.random() * 0.05); // 85-90% del precio
    
    // Nivel de confianza
    const confidence = 50 + Math.floor(Math.random() * 40); // 50-90%
    
    // Razones según acción
    let reason = '';
    if (action === 'BUY') {
      reason = `Identificamos una tendencia alcista en ${symbol} con soporte fuerte en ${(price * 0.95).toFixed(2)}. Las medias móviles indican momentum positivo y el RSI muestra condiciones de sobreventa recuperándose.`;
    } else if (action === 'SELL') {
      reason = `El análisis técnico de ${symbol} muestra señales de agotamiento alcista. RSI en zona de sobrecompra y divergencia con el precio. Recomendamos tomar beneficios o posición corta con gestión de riesgo.`;
    } else {
      reason = `Mercado de ${symbol} en fase de consolidación con volatilidad reducida. Recomendamos esperar confirmación de ruptura de rango antes de abrir nuevas posiciones.`;
    }
    
    // Indicadores
    const indicators = [
      { name: "RSI", value: 30 + Math.floor(Math.random() * 40) },
      { name: "MA20", value: price * (0.97 + Math.random() * 0.06) },
      { name: "MACD", value: (Math.random() * 0.2 - 0.1).toFixed(2) },
      { name: "Volumen", value: `${Math.floor(Math.random() * 150 + 50)}% del promedio` }
    ];
    
    // Timestamp (desde hace unas horas hasta hace un día)
    const timestamp = new Date(
      Date.now() - Math.random() * 86400000
    ).toISOString();
    
    recommendations.push({
      id: `rec-${i}-${Date.now()}`,
      symbol,
      action,
      price,
      timestamp,
      confidence,
      stopLoss,
      takeProfit,
      reason,
      indicators
    });
  }
  
  // Ordenar por timestamp (más reciente primero)
  return recommendations.sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
};